import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

const WEEK_JP = ["日", "月", "火", "水", "木", "金", "土"];

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function iso(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Schedule() {
  const [cursor, setCursor] = useState(new Date());
  const [visits, setVisits] = useState([]);
  const [patients, setPatients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    patient_id: "",
    staff_id: "",
    scheduled_date: iso(new Date()),
    start_time: "09:00",
    end_time: "10:00",
    service_type: "介護保険",
    note: "",
  });
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const start = useMemo(() => startOfMonth(cursor), [cursor]);
  const end = useMemo(() => endOfMonth(cursor), [cursor]);

  const load = () => {
    api.get("/visits", { params: { date_from: iso(start), date_to: iso(end) } }).then((r) => setVisits(r.data));
  };

  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data));
    api.get("/users").then((r) => setStaff(r.data));
  }, []);

  useEffect(load, [cursor]);

  const save = async () => {
    if (!form.patient_id) { toast.error("利用者を選択してください"); return; }
    await api.post("/visits", form);
    toast.success("訪問予定を追加しました");
    setOpen(false);
    load();
  };

  const del = async (id) => {
    if (!confirm("削除しますか?")) return;
    await api.delete(`/visits/${id}`);
    load();
  };

  const gridDays = useMemo(() => {
    const firstWeekDay = start.getDay();
    const total = end.getDate();
    const cells = [];
    for (let i = 0; i < firstWeekDay; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor, start, end]);

  const visitsByDate = useMemo(() => {
    const m = {};
    visits.forEach((v) => {
      (m[v.scheduled_date] ||= []).push(v);
    });
    return m;
  }, [visits]);

  const patientName = (pid) => patients.find((p) => p.id === pid)?.name_kanji || "?";
  const staffName = (sid) => staff.find((s) => s.id === sid)?.name || "未割当";

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A86] font-numeric mb-2">SCHEDULE</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">スケジュール</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-visit-button" className="bg-[#2C5545] text-white"><Plus size={16} className="mr-2" />訪問予定追加</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>訪問予定追加</DialogTitle></DialogHeader>
            <div className="space-y-3 py-3">
              <div>
                <Label>利用者</Label>
                <select value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input px-3 text-sm">
                  <option value="">選択してください</option>
                  {patients.map((p) => <option key={p.id} value={p.id}>{p.name_kanji}</option>)}
                </select>
              </div>
              <div>
                <Label>担当スタッフ</Label>
                <select value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input px-3 text-sm">
                  <option value="">未割当</option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div><Label>日付</Label><Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>開始</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                <div><Label>終了</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
              </div>
              <div>
                <Label>サービス区分</Label>
                <select value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} className="flex h-10 w-full rounded-md border border-input px-3 text-sm">
                  <option>介護保険</option><option>医療保険</option><option>精神科訪問看護</option><option>予防訪問看護</option>
                </select>
              </div>
              <div><Label>備考</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
              <Button data-testid="save-visit-button" className="bg-[#2C5545] text-white" onClick={save}>登録</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 md:p-6 border-[#E5E5E3] bg-white">
        <div className="flex items-center justify-between mb-4">
          <Button data-testid="prev-month" variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
            <ChevronLeft size={16} />
          </Button>
          <h2 className="text-lg font-bold font-numeric">
            {cursor.getFullYear()}年 {cursor.getMonth() + 1}月
          </h2>
          <Button data-testid="next-month" variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
            <ChevronRight size={16} />
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-xs text-center mb-2">
          {WEEK_JP.map((w, i) => (
            <div key={w} className={`py-1 font-medium ${i === 0 ? "text-[#A74141]" : i === 6 ? "text-[#4A6B8C]" : "text-[#4A4A48]"}`}>{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {gridDays.map((d, i) => (
            <div key={i} className={`min-h-24 p-1 border border-[#E5E5E3] rounded ${d ? "bg-white" : "bg-[#F3F3F1]"}`}>
              {d && (
                <>
                  <div className={`text-xs font-numeric mb-1 ${i % 7 === 0 ? "text-[#A74141]" : i % 7 === 6 ? "text-[#4A6B8C]" : ""}`}>{d.getDate()}</div>
                  <div className="space-y-0.5">
                    {(visitsByDate[iso(d)] || []).slice(0, 3).map((v) => (
                      <button
                        key={v.id}
                        onClick={() => del(v.id)}
                        className="w-full text-[10px] bg-[#D9E2D5] text-[#2C5545] rounded px-1 py-0.5 truncate text-left hover:bg-[#C5D5BD]"
                        title={`${v.start_time} ${patientName(v.patient_id)} (${staffName(v.staff_id)})`}
                      >
                        {v.start_time} {patientName(v.patient_id)}
                      </button>
                    ))}
                    {(visitsByDate[iso(d)]?.length || 0) > 3 && (
                      <div className="text-[10px] text-[#8A8A86]">+{visitsByDate[iso(d)].length - 3}件</div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 border-[#E5E5E3] bg-white">
        <h3 className="text-lg font-bold mb-4">今月の訪問一覧 ({visits.length}件)</h3>
        <div className="divide-y divide-[#E5E5E3]">
          {visits.map((v) => (
            <div key={v.id} className="py-3 flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-medium font-numeric">{v.scheduled_date} {v.start_time}-{v.end_time}</div>
                <div className="text-sm text-[#4A4A48]">{patientName(v.patient_id)} · {staffName(v.staff_id)}</div>
              </div>
              <Badge variant="outline">{v.service_type}</Badge>
              <button onClick={() => del(v.id)} className="text-[#A74141]"><Trash2 size={14} /></button>
            </div>
          ))}
          {visits.length === 0 && <div className="text-center py-8 text-[#8A8A86]">訪問予定がありません</div>}
        </div>
      </Card>
    </div>
  );
}
